import apiClient from './api';

export interface GymCreationData {
  name: string;
  description?: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  latitude: number;
  longitude: number;
  phone: string;
  email?: string;
  opening_time: string;
  closing_time: string;
  total_capacity: number;
  amenities?: string[];
}

export interface GymUpdateData {
  name?: string;
  description?: string;
  opening_time?: string;
  closing_time?: string;
  total_capacity?: number;
  amenities?: string[];
}

export interface GymDetails {
  id: number;
  partner_id: number;
  name: string;
  description?: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  latitude: number;
  longitude: number;
  phone: string;
  email?: string;
  opening_time: string;
  closing_time: string;
  total_capacity: number;
  current_occupancy: number;
  amenities: string[];
  images?: string[];
  rating: number;
  total_reviews: number;
  is_active: boolean;
  is_verified: boolean;
  verification_date?: string;
  created_at: string;
  updated_at: string;
}

export interface GymAnalytics {
  gym_id: number;
  gym_name: string;
  period: {
    start_date: string;
    end_date: string;
  };
  summary: {
    total_bookings: number;
    completed_bookings: number;
    total_revenue: number;
    coins_revenue: number;
    cash_revenue: number;
    average_occupancy: number;
    unique_users: number;
  };
  top_time_slots: Array<{
    start_time: string;
    end_time: string;
    bookings: number;
    revenue: number;
  }>;
  daily_bookings: Array<{
    date: string;
    bookings: number;
    revenue: number;
  }>;
  reviews_summary: {
    average_rating: number;
    total_reviews: number;
    rating_distribution: {
      [key: number]: number;
    };
  };
}

class GymService {
  /**
   * Create new gym
   */
  async createGym(data: GymCreationData): Promise<{ gymId: number; message: string; gym: GymDetails }> {
    return apiClient.post<{ gymId: number; message: string; gym: GymDetails }>('/partners/gyms', data);
  }

  /**
   * Get all gyms for partner
   */
  async getPartnerGyms(): Promise<{ gyms: GymDetails[]; total_count: number }> {
    return apiClient.get<{ gyms: GymDetails[]; total_count: number }>('/partners/gyms');
  }

  /**
   * Get single gym details
   */
  async getGymDetails(gymId: number): Promise<GymDetails> {
    return apiClient.get<GymDetails>(`/partners/gyms/${gymId}`);
  }

  /**
   * Update gym
   */
  async updateGym(gymId: number, data: GymUpdateData): Promise<{ message: string; gym: GymDetails }> {
    return apiClient.put<{ message: string; gym: GymDetails }>(`/partners/gyms/${gymId}`, data);
  }

  /**
   * Delete gym
   */
  async deleteGym(gymId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/partners/gyms/${gymId}`);
  }

  /**
   * Get gym analytics
   */
  async getGymAnalytics(gymId: number, startDate?: string, endDate?: string): Promise<GymAnalytics> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    return apiClient.get<GymAnalytics>(
      `/partners/gyms/${gymId}/analytics${params.toString() ? '?' + params.toString() : ''}`
    );
  }
}

const gymService = new GymService();
export default gymService;
